import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import "./index.css";
import {
  C, RANGES, SECTIONS, Icon, fmtNum, fmtLarge, getDateRange,
  fetchSymbolDetail, fetchChartData, fetchIntradayData, fetchAnnouncements, fetchFinancials,
} from "./shared";
import TechnicalSection from "./sections/TechnicalSection";
import ChartSection from "./sections/ChartSection";
import PerformanceSection from "./sections/PerformanceSection";
import AboutSection from "./sections/AboutSection";
import AnnouncementsSection from "./sections/AnnouncementsSection";
import EarningsSection from "./sections/EarningsSection";
import EpsSection from "./sections/EpsSection";
import DividendsSection from "./sections/DividendsSection";
import FinancialsSection from "./sections/FinancialsSection";
import OhlcvSection from "./sections/OhlcvSection";

const PSXDashboard = () => {
  const { symbol } = useParams();
  const history = useHistory();

  const [symbolData, setSymbolData]   = useState(null);
  const [chartData, setChartData]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError]             = useState(null);
  const [activeRange, setActiveRange] = useState(RANGES[0]);
  const [announcements, setAnnouncements] = useState([]);
  const [financials, setFinancials]   = useState(null);

  const [headerOffset, setHeaderOffset] = useState(134);
  const [activeSection, setActiveSection] = useState("technical");

  // section refs
  const refs = {
    technical:     useRef(null),
    chart:         useRef(null),
    performance:   useRef(null),
    about:         useRef(null),
    announcements: useRef(null),
    earnings:      useRef(null),
    eps:           useRef(null),
    dividends:     useRef(null),
    financials:    useRef(null),
    ohlcv:         useRef(null),
  };

  // measure fixed site-header height
  useEffect(() => {
    const measure = () => {
      const off = document.querySelector(".hdr-offset");
      if (off) {
        const pt = parseInt(getComputedStyle(off).paddingTop, 10);
        if (!isNaN(pt)) setHeaderOffset(pt);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // data
  useEffect(() => {
    if (!symbol) return;
    setLoading(true); setError(null);
    fetchSymbolDetail(symbol).then(setSymbolData).catch(e => setError(e.message)).finally(() => setLoading(false));
    fetchAnnouncements(symbol).then(d => setAnnouncements(d.results || [])).catch(() => setAnnouncements([]));
    fetchFinancials(symbol).then(setFinancials).catch(() => setFinancials(null));
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    setChartLoading(true);
    const loader = activeRange.days === 0
      ? fetchIntradayData(symbol)
      : (() => { const { from, to } = getDateRange(activeRange.days); return fetchChartData(symbol, from, to); })();
    loader.then(setChartData).catch(() => setChartData([])).finally(() => setChartLoading(false));
  }, [symbol, activeRange]);

  // scroll-spy (throttled, only sets when changed)
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      const threshold = headerOffset + 90;
      let current = null;
      SECTIONS.forEach(({ id }) => {
        const el = refs[id]?.current;
        if (el && el.getBoundingClientRect().top <= threshold) current = id;
      });
      if (current) setActiveSection(prev => (prev === current ? prev : current));
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerOffset, financials, announcements]);

  const scrollTo = (id) => {
    const el = refs[id]?.current;
    if (!el) return;
    const y = window.scrollY + el.getBoundingClientRect().top - headerOffset - 16;
    window.scrollTo({ top: y, behavior: "smooth" });
    setActiveSection(id);
  };

  if (loading) return (
    <div className="tdash" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: C.orange }}>Loading {symbol}…</div>
    </div>
  );
  if (error || !symbolData) return (
    <div className="tdash" style={{ padding: "5rem 2rem", textAlign: "center" }}>
      <div style={{ color: C.red, marginBottom: 16 }}>{error || "Symbol not found"}</div>
      <button onClick={() => history.push("/")} style={{
        background: C.orange, color: "#1a1205", border: "none",
        padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700,
      }}>← Go Home</button>
    </div>
  );

  const d       = symbolData;
  const details = d.details || {};
  const stats   = d.stats || {};
  const ranges  = stats.ranges || {};
  const perf    = stats.performance || {};

  const lastPrice = stats.last_price != null ? parseFloat(stats.last_price) : null;
  const changeVal = stats.change_val != null ? parseFloat(stats.change_val) : null;
  const changePercent = stats.change_percent != null ? parseFloat(stats.change_percent) : null;
  const isPos = changePercent != null ? changePercent >= 0 : true;

  // which sidebar items to show (based on available data)
  const available = {
    technical: true, chart: true, performance: true, about: true,
    announcements: announcements.length > 0,
    earnings: !!financials?.earnings,
    eps: !!financials?.earnings?.quarterly_chart?.length,
    dividends: !!financials?.dividends,
    financials: !!financials?.profit?.history?.length,
    ohlcv: true,
  };
  const navItems = SECTIONS.filter(s => available[s.id]);

  return (
    <div className="tdash">
      <div className="tdash-wrap">

        {/* ── HEADER ── */}
        <div className="tdash-header">
          <div className="tdash-h-left">
            <div className="tdash-logo">
              <img
                src={`https://api.orbitsecurities.com.pk/images/symbols/${d.code}.svg`}
                alt={d.code}
                onError={e => {
                  e.target.style.display = "none";
                  e.target.parentNode.innerHTML =
                    `<span style="font-size:14px;font-weight:700;color:${C.orange};font-family:monospace">${d.code}</span>`;
                }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span className="tdash-code">{d.code}</span>
                <h1 className="tdash-name">{d.name}</h1>
              </div>
              <p className="tdash-sector">
                {d.sector?.name || "—"}
                {d.is_shariah && <span className="tdash-tag shariah">✓ Shariah</span>}
                {!d.is_active && <span className="tdash-tag inactive">Inactive</span>}
              </p>
            </div>
          </div>

          <div className="tdash-price-box">
            <div className="tdash-price">{lastPrice != null ? `Rs. ${fmtNum(lastPrice)}` : "N/A"}</div>
            {changePercent != null && (
              <div>
                <span className={`tdash-chg ${isPos ? "pos" : "neg"}`}>
                  {isPos ? "▲" : "▼"} {changeVal.toFixed(2)} ({changePercent.toFixed(2)}%)
                </span>
              </div>
            )}
            {stats.volume && <div className="tdash-vol">Vol: {parseInt(stats.volume).toLocaleString()}</div>}
          </div>
        </div>

        {/* ── MAIN: SIDEBAR + CONTENT ── */}
        <div className="tdash-main">

          <aside className="tdash-sidebar" style={{ top: headerOffset + 16 }}>
            <nav className="tdash-nav">
              {navItems.map(s => (
                <button
                  key={s.id}
                  className={`tdash-nav-item${activeSection === s.id ? " active" : ""}`}
                  onClick={() => scrollTo(s.id)}
                >
                  <span className="tdash-nav-ic"><Icon name={s.icon} size={17} /></span>
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>

          <div className="tdash-content">

            {/* stat strip */}
            <TechnicalSection ref={refs.technical} code={d.code} />
            <div className="tdash-stats">
              <Stat label="Latest Price"  value={lastPrice ? `Rs. ${fmtNum(lastPrice)}` : "—"} color={isPos ? C.green : C.red}
                    sub={changePercent != null ? `${isPos ? "+" : ""}${fmtNum(changeVal)} (${isPos ? "+" : ""}${fmtNum(changePercent)}%)` : null} subColor={isPos ? C.green : C.red} />
              <Stat label="Previous Close" value={stats.ldcp ? fmtNum(stats.ldcp) : "—"} />
              <Stat label="Today's Volume" value={stats.volume ? parseInt(stats.volume).toLocaleString() : "—"} />
              <Stat label="Avg Vol (12M)"  value={stats.avg_volume ? parseInt(stats.avg_volume).toLocaleString() : "—"} />
              <Stat label="Total Shares"   value={fmtLarge(details.total_shares)} />
              <Stat label="Free Float"     value={fmtLarge(details.free_float)}
                    sub={details.free_float && details.total_shares ? `${((details.free_float / details.total_shares) * 100).toFixed(1)}% of total` : null} subColor={C.muted} />
              <Stat label="Face Value"     value={details.face_value ? `Rs. ${details.face_value}` : "—"} />
              <Stat label="Year End"       value={details.year_end || "—"} />
            </div>

            <PerformanceSection ref={refs.performance} ranges={ranges} perf={perf} lastPrice={lastPrice} />

            <ChartSection ref={refs.chart} code={d.code} chartData={chartData} chartLoading={chartLoading} activeRange={activeRange} setActiveRange={setActiveRange} />
            <AboutSection ref={refs.about} d={d} details={details} />
            {available.announcements && <AnnouncementsSection ref={refs.announcements} announcements={announcements} />}
            {available.earnings && <EarningsSection ref={refs.earnings} earnings={financials.earnings} />}
            {available.eps && <EpsSection ref={refs.eps} quarterly={financials.earnings.quarterly_chart} />}
            {available.dividends && <DividendsSection ref={refs.dividends} dividends={financials.dividends} />}
            {available.financials && <FinancialsSection ref={refs.financials} profit={financials.profit} />}
            <OhlcvSection ref={refs.ohlcv} chartData={chartData} activeRange={activeRange} setActiveRange={setActiveRange} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, sub, color, subColor }) => (
  <div className="tdash-stat">
    <div className="tdash-stat-label">{label}</div>
    <div className="tdash-stat-value" style={color ? { color } : undefined}>{value ?? "—"}</div>
    {sub && <div className="tdash-stat-sub" style={{ color: subColor || C.muted }}>{sub}</div>}
  </div>
);

export default PSXDashboard;
